import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Shield, Brain, TrendingDown, BarChart3, Zap, CheckCircle, AlertTriangle, DollarSign, Car, Mic, Clock, Target, Award, Globe, FileCheck, ArrowRight, ArrowDown, Layers, Cpu, Activity, Eye, EyeOff, Users, Wrench, PlusCircle, Video, Smartphone, Languages } from "lucide-react";
import T from "./pitchDeckTranslations";

/* ── Decorative background shapes ── */
const FloatingOrbs = ({ colors = ["#FFD700", "#00BFFF", "#FF6B6B"], opacity = 0.12 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: colors[0], opacity }} />
    <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full blur-[140px]" style={{ background: colors[1], opacity }} />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[100px]" style={{ background: colors[2], opacity: opacity * 0.6 }} />
  </div>
);

const GridPattern = ({ color = "rgba(255,255,255,0.04)" }) => (
  <div className="absolute inset-0 pointer-events-none" style={{
    backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
    backgroundSize: "60px 60px",
  }} />
);

const DiagonalLines = ({ color = "rgba(255,255,255,0.03)" }) => (
  <div className="absolute inset-0 pointer-events-none" style={{
    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 40px, ${color} 40px, ${color} 41px)`,
  }} />
);

/* ── Glassmorphism card ── */
const GlassCard = ({ children, className = "", dark = false }) => (
  <div className={`rounded-2xl border ${dark
    ? "bg-white/[0.06] border-white/[0.1] backdrop-blur-xl"
    : "bg-white/70 border-white/40 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
    } ${className}`}>
    {children}
  </div>
);

/* ── Gradient text ── */
const GradientText = ({ children, className = "", from = "#FFD700", to = "#FFA500" }) => (
  <span className={`bg-clip-text text-transparent ${className}`}
    style={{ backgroundImage: `linear-gradient(135deg, ${from}, ${to})` }}>
    {children}
  </span>
);

/* ── Metric pill ── */
const MetricPill = ({ value, label, dark = true }) => (
  <div className={`rounded-2xl p-6 text-center ${dark
    ? "bg-gradient-to-br from-white/[0.08] to-white/[0.03] border border-white/[0.1] backdrop-blur-xl"
    : "bg-gradient-to-br from-slate-50 to-white border border-slate-200/80 shadow-lg"
    }`}>
    <p className={`text-4xl font-black tracking-tight ${dark ? "" : "text-slate-900"}`}>
      {dark ? <GradientText from="#FFD700" to="#FFAA00">{value}</GradientText> : <GradientText from="#003399" to="#0066CC">{value}</GradientText>}
    </p>
    <p className={`text-sm mt-2 font-medium ${dark ? "text-blue-200/80" : "text-slate-500"}`}>{label}</p>
  </div>
);

/* ── Badge ── */
const Badge = ({ children, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-500/10 text-blue-300 border-blue-400/20",
    gold: "bg-yellow-500/10 text-yellow-300 border-yellow-400/20",
    green: "bg-emerald-500/10 text-emerald-300 border-emerald-400/20",
    red: "bg-red-500/10 text-red-400 border-red-400/20",
    white: "bg-white/10 text-white/80 border-white/20",
    cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-400/20",
  };
  return (
    <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border ${colors[color]}`}>
      {children}
    </span>
  );
};

/* ══════════════════════════════════════════
   SLIDES (10 total)
   ══════════════════════════════════════════ */

const SLIDES = [
  // ── SLIDE 0: Title ──
  {
    id: "title",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #000B2E 0%, #001B6E 40%, #003399 70%, #0052CC 100%)" }}>
        <FloatingOrbs colors={["#FFD700", "#0066FF", "#00AAFF"]} opacity={0.15} />
        <GridPattern />
        <div className="absolute top-1/2 right-[8%] -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-white/[0.06]" />
        <div className="absolute top-1/2 right-[8%] -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/[0.04] translate-x-8 translate-y-4" />
        <div className="absolute top-1/2 right-[8%] -translate-y-1/2 w-[300px] h-[300px] rounded-full border border-white/[0.03] translate-x-16 translate-y-8" />

        <div className="relative flex flex-col items-start justify-center h-full text-white pl-20 pr-20 z-10">
          <div className="mb-10">
            <img src="/wisedrive-logo-deck.png" alt="Wisedrive" className="h-14 drop-shadow-2xl" />
          </div>
          <h1 className="text-7xl font-black tracking-tight leading-[1.05] mb-4">
            {t.title_main}<br />
            <GradientText from="#FFD700" to="#FFAA00" className="text-7xl font-black">{t.title_sub}</GradientText>
          </h1>
          <h2 className="text-2xl font-light text-blue-200/80 mt-2 max-w-xl">
            {t.title_desc}
          </h2>
          <div className="flex items-center gap-4 mt-12">
            <Badge color="gold">{t.badge_patented}</Badge>
            <Badge color="white">{t.badge_confidential}</Badge>
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 1: The Problem ──
  {
    id: "problem",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(160deg, #0F0F0F 0%, #1A1A2E 50%, #16213E 100%)" }}>
        <FloatingOrbs colors={["#FF4444", "#FF6B6B", "#CC0000"]} opacity={0.08} />
        <DiagonalLines color="rgba(255,255,255,0.02)" />

        <div className="relative flex flex-col h-full px-20 py-14 z-10">
          <Badge color="red">{t.problem_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-10 tracking-tight">
            {t.problem_title} <GradientText from="#FF4444" to="#FF8888">{t.problem_title_accent}</GradientText>
          </h2>
          <div className="grid grid-cols-3 gap-6 flex-1">
            {[
              { icon: Clock, title: t.problem_card1_title, desc: t.problem_card1_desc, accent: "#FF6B6B" },
              { icon: AlertTriangle, title: t.problem_card2_title, desc: t.problem_card2_desc, accent: "#FFB347" },
              { icon: DollarSign, title: t.problem_card3_title, desc: t.problem_card3_desc, accent: "#FF4444" },
            ].map((item, i) => (
              <GlassCard key={i} dark className="p-8 flex flex-col group hover:bg-white/[0.1] transition-all duration-500">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${item.accent}20`, border: `1px solid ${item.accent}30` }}>
                  <item.icon size={26} style={{ color: item.accent }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed text-[15px]">{item.desc}</p>
              </GlassCard>
            ))}
          </div>
          <GlassCard dark className="mt-6 p-5 text-center">
            <p className="text-lg text-white/90">
              {t.problem_stat[0]} <GradientText from="#FFD700" to="#FFA500" className="font-black text-xl">{t.problem_stat[1]}</GradientText> {t.problem_stat[2]} <GradientText from="#FFD700" to="#FFA500" className="font-black text-xl">{t.problem_stat[3]}</GradientText> {t.problem_stat[4]} <GradientText from="#FFD700" to="#FFA500" className="font-black text-xl">{t.problem_stat[5]}</GradientText> {t.problem_stat[6]}
            </p>
          </GlassCard>
        </div>
      </div>
    ),
  },

  // ── SLIDE 2: Fits Your Existing Process (NEW) ──
  {
    id: "existing-process",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(160deg, #080C1A 0%, #0D1B3E 40%, #132B5E 100%)" }}>
        <FloatingOrbs colors={["#FF4444", "#10B981", "#FFD700"]} opacity={0.1} />
        <GridPattern color="rgba(255,255,255,0.025)" />

        <div className="relative flex flex-col h-full px-20 py-12 z-10">
          <Badge color="cyan">{t.process_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-8 tracking-tight">
            {t.process_title} <GradientText from="#00BFFF" to="#00E5FF">{t.process_title_accent}</GradientText>
          </h2>

          <div className="grid grid-cols-2 gap-6 flex-1">
            {/* Current Process - Dark Red Side */}
            <div className="rounded-3xl overflow-hidden flex flex-col relative" style={{ background: "linear-gradient(160deg, rgba(239,68,68,0.08), rgba(239,68,68,0.02))", border: "1px solid rgba(239,68,68,0.15)" }}>
              <div className="px-6 py-3.5 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(239,68,68,0.12)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                  <span className="text-sm font-bold text-red-400/80 uppercase tracking-wider">{t.process_today}</span>
                </div>
                <span className="text-xs text-red-400/40 font-medium">{t.process_today_sub}</span>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-center space-y-3">
                {[
                  { icon: Users, text: t.process_step_visit, sub: "Standard field visit", dim: true },
                  { icon: Eye, text: t.process_step_visual, sub: "Exterior & basic engine look", dim: true },
                  { icon: Video, text: t.process_step_video_old, sub: t.process_step_video_old_sub, highlight: true },
                  { icon: FileCheck, text: t.process_step_subjective, sub: t.process_step_subjective_sub, dim: true },
                ].map((step, i) => (
                  <div key={i} className={`flex items-center gap-4 rounded-xl px-4 py-3 transition-all ${step.highlight ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/[0.03] border border-white/[0.05]'}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${step.highlight ? 'bg-red-500/20 border border-red-500/30' : 'bg-white/[0.06] border border-white/[0.08]'}`}>
                      <step.icon size={18} className={step.highlight ? "text-red-400" : "text-slate-500"} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-[14px] font-semibold ${step.highlight ? 'text-red-400' : 'text-slate-300'}`}>{step.text}</p>
                      <p className="text-[11px] text-slate-500">{step.sub}</p>
                    </div>
                    {step.highlight && (
                      <span className="text-[10px] bg-red-500/20 text-red-400 px-3 py-1.5 rounded-full font-black uppercase tracking-wider border border-red-500/30 shadow-[0_0_12px_rgba(239,68,68,0.15)]">{t.process_wasted}</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Dim X overlay on bottom */}
              <div className="px-6 py-3 text-center" style={{ borderTop: "1px solid rgba(239,68,68,0.1)" }}>
                <p className="text-xs text-red-400/50 font-medium">{t.process_result_old}</p>
              </div>
            </div>

            {/* Enhanced Process - Vibrant Green Side */}
            <div className="rounded-3xl overflow-hidden flex flex-col relative" style={{ background: "linear-gradient(160deg, rgba(16,185,129,0.1), rgba(16,185,129,0.02))", border: "1px solid rgba(16,185,129,0.2)" }}>
              {/* Glow effect */}
              <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px]" style={{ background: "#10B981", opacity: 0.1 }} />
              <div className="px-6 py-3.5 flex items-center justify-between relative z-10" style={{ borderBottom: "1px solid rgba(16,185,129,0.15)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                  <span className="text-sm font-bold text-emerald-400/90 uppercase tracking-wider">{t.process_enhanced}</span>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.15)]">{t.process_wisedrive}</span>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-center space-y-3 relative z-10">
                {[
                  { icon: Users, text: t.process_step_visit, sub: "No change", same: true },
                  { icon: Eye, text: t.process_step_visual, sub: "Same protocol", same: true },
                  { icon: Smartphone, text: t.process_step_record_new, sub: t.process_step_record_sub, mid: true },
                  { icon: Brain, text: t.process_step_ai_score, sub: t.process_step_ai_sub, isNew: true },
                  { icon: FileCheck, text: t.process_step_objective, sub: t.process_step_objective_sub, isNew: true },
                ].map((step, i) => (
                  <div key={i} className={`flex items-center gap-4 rounded-xl px-4 py-3 transition-all ${step.isNew ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/[0.03] border border-white/[0.05]'}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${step.isNew ? 'border border-emerald-500/30' : 'bg-white/[0.06] border border-white/[0.08]'}`} style={step.isNew ? { background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.1))' } : {}}>
                      <step.icon size={18} className={step.isNew ? "text-emerald-400" : step.mid ? "text-blue-400" : "text-slate-500"} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-[14px] font-semibold ${step.isNew ? 'text-emerald-400' : 'text-slate-300'}`}>{step.text}</p>
                      <p className="text-[11px] text-slate-500">{step.sub}</p>
                    </div>
                    {step.same && <span className="text-[10px] text-slate-500/50 font-medium">{t.process_unchanged}</span>}
                    {step.isNew && <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2.5 py-1 rounded-full font-black border border-emerald-500/30">+ AI</span>}
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 text-center relative z-10" style={{ borderTop: "1px solid rgba(16,185,129,0.12)" }}>
                <p className="text-xs text-emerald-400/60 font-medium">{t.process_result_new}</p>
              </div>
            </div>
          </div>

          <GlassCard dark className="mt-4 p-4 text-center">
            <p className="text-lg text-white/90">
              <span className="font-black text-white">{t.process_bottom[0]}</span> {" "} <GradientText from="#FFD700" to="#FFAA00" className="font-black">{t.process_bottom[1]}</GradientText>.
            </p>
          </GlassCard>
        </div>
      </div>
    ),
  },

  // ── SLIDE 3: Empowering Inspectors + Better Pricing (NEW) ──
  {
    id: "empower",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #000B2E 0%, #001B6E 40%, #002B8F 100%)" }}>
        <FloatingOrbs colors={["#10B981", "#FFD700", "#0088FF"]} opacity={0.1} />
        <GridPattern />

        <div className="relative flex flex-col h-full px-20 py-12 z-10">
          <Badge color="green">{t.empower_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-8 tracking-tight">
            {t.empower_title} <GradientText from="#10B981" to="#34D399">{t.empower_accent}</GradientText>
          </h2>

          <div className="grid grid-cols-2 gap-8 flex-1">
            {/* Left: Inspector empowerment */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white/90 mb-2 flex items-center gap-3">
                <Users size={22} className="text-blue-400" /> {t.empower_left_title}
              </h3>
              {[
                { icon: Eye, title: t.empower_eyes, desc: t.empower_eyes_desc, color: "#60A5FA" },
                { icon: Brain, title: t.empower_ai, desc: t.empower_ai_desc, color: "#A78BFA" },
                { icon: Shield, title: t.empower_together, desc: t.empower_together_desc, color: "#10B981" },
              ].map((item, i) => (
                <GlassCard key={i} dark className="p-5 flex gap-4 items-start group hover:bg-white/[0.08] transition-all duration-500">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}15`, border: `1px solid ${item.color}25` }}>
                    <item.icon size={18} style={{ color: item.color }} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-[15px]">{item.title}</h4>
                    <p className="text-sm text-slate-400 leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Right: Better Pricing & Valuation */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white/90 mb-2 flex items-center gap-3">
                <DollarSign size={22} className="text-yellow-400" /> {t.empower_right_title}
              </h3>
              <GlassCard dark className="p-6">
                <p className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">How AI Improves Valuation Accuracy</p>
                <div className="space-y-5">
                  {[
                    { label: t.empower_without, sub: t.empower_without_sub, items: ["Inspector opinion only", "Wide variance between evaluators", "Hidden engine problems go undetected", "Overvaluation risk → inflated LTV"], bad: true },
                    { label: t.empower_with, sub: t.empower_with_sub, items: ["Precise health score (1-10)", "Same video = same score, every time", "Internal wear detected early", "Accurate LTV → lower NPA risk"], bad: false },
                  ].map((block, i) => (
                    <div key={i}>
                      <p className={`text-sm font-bold mb-1 ${block.bad ? 'text-red-400' : 'text-emerald-400'}`}>{block.label}</p>
                      <p className="text-xs text-slate-500 mb-2">{block.sub}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {block.items.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 text-xs">
                            {block.bad
                              ? <AlertTriangle size={11} className="text-red-400/60 flex-shrink-0" />
                              : <CheckCircle size={11} className="text-emerald-400/80 flex-shrink-0" />
                            }
                            <span className="text-slate-400">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
              <GlassCard dark className="p-4 text-center">
                <p className="text-white font-bold text-lg">
                  Result: <GradientText from="#FFD700" to="#FFAA00">{t.empower_result}</GradientText>
                </p>
              </GlassCard>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 4: Complete Vehicle Intelligence Platform (NEW) ──
  {
    id: "platform",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #0A0F2E 0%, #111B45 40%, #162355 100%)" }}>
        <FloatingOrbs colors={["#3B82F6", "#10B981", "#F59E0B"]} opacity={0.1} />
        <GridPattern color="rgba(255,255,255,0.02)" />

        <div className="relative flex flex-col h-full px-20 py-12 z-10">
          <Badge color="gold">{t.platform_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-6 tracking-tight">
            {t.platform_title} <GradientText from="#FFD700" to="#FFAA00">{t.platform_accent}</GradientText>
          </h2>
          <p className="text-lg text-slate-400 mb-7 max-w-3xl">{t.platform_desc}</p>

          <div className="grid grid-cols-3 gap-6 flex-1">
            {/* Sound AI */}
            <div className="rounded-3xl overflow-hidden flex flex-col relative" style={{ background: "linear-gradient(160deg, rgba(59,130,246,0.12), rgba(59,130,246,0.02))", border: "1px solid rgba(59,130,246,0.2)" }}>
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full blur-[70px] opacity-20" style={{ background: "#3B82F6" }} />
              <div className="p-7 flex-1 flex flex-col relative z-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1E40AF, #3B82F6)", boxShadow: "0 8px 24px rgba(59,130,246,0.3)" }}>
                    <Mic size={26} className="text-white" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">{t.platform_live}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t.platform_sound}</h3>
                <p className="text-sm text-blue-200/50 mb-3">{t.platform_sound_sub}</p>
                <p className="text-slate-400 text-[13px] leading-relaxed mb-4">{t.platform_sound_desc}</p>
                <div className="mt-auto rounded-xl p-3 bg-white/[0.04] border border-white/[0.06]">
                  <div className="flex items-end justify-center gap-[3px] h-10">
                    {[30,50,35,65,45,70,40,55,30,60,45,75,35,50,40,65,30,55,45,70,35,60,50,45,30,55,40,65,35,50].map((h, i) => (
                      <div key={i} className="w-[3px] rounded-full" style={{
                        height: `${h}%`,
                        background: i % 3 === 0 ? '#3B82F6' : i % 3 === 1 ? '#60A5FA' : '#93C5FD',
                        opacity: 0.4 + (h / 100) * 0.6,
                      }} />
                    ))}
                  </div>
                  <p className="text-[10px] text-blue-300/40 text-center mt-2 font-medium">{t.platform_audio_freq}</p>
                </div>
              </div>
            </div>

            {/* Vision AI */}
            <div className="rounded-3xl overflow-hidden flex flex-col relative" style={{ background: "linear-gradient(160deg, rgba(16,185,129,0.12), rgba(16,185,129,0.02))", border: "1px solid rgba(16,185,129,0.2)" }}>
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full blur-[70px] opacity-20" style={{ background: "#10B981" }} />
              <div className="p-7 flex-1 flex flex-col relative z-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #047857, #10B981)", boxShadow: "0 8px 24px rgba(16,185,129,0.3)" }}>
                    <Eye size={26} className="text-white" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">{t.platform_live}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t.platform_vision}</h3>
                <p className="text-sm text-emerald-200/50 mb-3">{t.platform_vision_sub}</p>
                <p className="text-slate-400 text-[13px] leading-relaxed mb-4">{t.platform_vision_desc}</p>
                <div className="mt-auto rounded-xl p-3 bg-white/[0.04] border border-white/[0.06]">
                  <div className="grid grid-cols-4 gap-1.5 h-10">
                    {["Front", "Rear", "Left", "Right"].map((side, i) => (
                      <div key={i} className="rounded-md flex items-center justify-center text-[9px] font-bold" style={{
                        background: i === 2 ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.1)',
                        border: `1px solid ${i === 2 ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.15)'}`,
                        color: i === 2 ? '#FBBF24' : '#34D399',
                      }}>
                        {side} {i === 2 ? '!' : '\u2713'}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-emerald-300/40 text-center mt-2 font-medium">{t.platform_damage_scan}</p>
                </div>
              </div>
            </div>

            {/* OBD Scanner */}
            <div className="rounded-3xl overflow-hidden flex flex-col relative" style={{ background: "linear-gradient(160deg, rgba(245,158,11,0.12), rgba(245,158,11,0.02))", border: "1px solid rgba(245,158,11,0.2)" }}>
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full blur-[70px] opacity-20" style={{ background: "#F59E0B" }} />
              <div className="p-7 flex-1 flex flex-col relative z-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #B45309, #F59E0B)", boxShadow: "0 8px 24px rgba(245,158,11,0.3)" }}>
                    <Cpu size={26} className="text-white" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">{t.platform_live}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{t.platform_obd}</h3>
                <p className="text-sm text-yellow-200/50 mb-3">{t.platform_obd_sub}</p>
                <p className="text-slate-400 text-[13px] leading-relaxed mb-4">{t.platform_obd_desc}</p>
                <div className="mt-auto rounded-xl p-3 bg-white/[0.04] border border-white/[0.06]">
                  <div className="grid grid-cols-3 gap-1.5 h-10">
                    {[
                      { label: "ECU", ok: true }, { label: "ABS", ok: true }, { label: "O2", ok: false },
                      { label: "BAT", ok: true }, { label: "TRN", ok: true }, { label: "ENG", ok: true },
                    ].map((item, i) => (
                      <div key={i} className="rounded-md flex items-center justify-center gap-1 text-[9px] font-bold" style={{
                        background: item.ok ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.15)',
                        border: `1px solid ${item.ok ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.25)'}`,
                        color: item.ok ? '#34D399' : '#FBBF24',
                      }}>
                        {item.label} {item.ok ? '\u2713' : '!'}
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-yellow-300/40 text-center mt-2 font-medium">{t.platform_elec_params}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 5: Sample Report ──
  {
    id: "sample",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(160deg, #0A0A1A 0%, #0F1629 50%, #141E33 100%)" }}>
        <FloatingOrbs colors={["#10B981", "#3B82F6", "#FFD700"]} opacity={0.08} />
        <GridPattern color="rgba(255,255,255,0.02)" />

        <div className="relative flex flex-col h-full px-20 py-12 z-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Badge color="green">{t.sample_badge}</Badge>
              <h2 className="text-4xl font-black text-white mt-3 tracking-tight">
                {t.sample_title} <GradientText from="#10B981" to="#34D399">{t.sample_car}</GradientText>
              </h2>
              <p className="text-slate-500 mt-1 text-lg">{t.sample_specs}</p>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-5 flex-1">
            {/* Left - Sound bars (3 cols) */}
            <GlassCard dark className="col-span-3 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">{t.sample_sound_title}</p>
              {[
                { name: "Engine", ideal: 45, measured: 32, ok: true },
                { name: "Light engine (high freq)", ideal: 38, measured: 35, ok: true },
                { name: "Medium engine (mid freq)", ideal: 30, measured: 42, ok: false },
                { name: "Heavy engine (low freq)", ideal: 25, measured: 28, ok: false },
                { name: "Engine knocking", ideal: 35, measured: 22, ok: true },
                { name: "Idling", ideal: 40, measured: 33, ok: true },
                { name: "Accelerating", ideal: 35, measured: 38, ok: false },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3 py-[7px]">
                  {s.ok
                    ? <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                    : <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
                  }
                  <span className="text-xs text-slate-400 w-40 font-medium">{s.name}</span>
                  <div className="flex-1 relative h-6 rounded-md overflow-hidden bg-white/[0.04]">
                    {s.ok ? (
                      <>
                        <div className="absolute inset-y-0 left-0 rounded-md" style={{ width: `${(s.measured / 60) * 100}%`, background: 'linear-gradient(90deg, #10B981, #34D399)' }} />
                        <div className="absolute inset-y-0 rounded-md" style={{ left: `${(s.measured / 60) * 100}%`, width: `${((s.ideal - s.measured) / 60) * 100}%`, background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 2px, transparent 2px, transparent 5px)' }} />
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-y-0 left-0 rounded-md" style={{ width: `${(s.measured / 60) * 100}%`, background: 'linear-gradient(90deg, #F59E0B, #FBBF24)' }} />
                        <div className="absolute inset-y-0 left-0 rounded-md" style={{ width: `${(s.ideal / 60) * 100}%`, background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.08), rgba(255,255,255,0.08) 2px, transparent 2px, transparent 5px)' }} />
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex gap-5 mt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-emerald-400" /> {t.sample_within}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-amber-400" /> {t.sample_exceeds}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm" style={{ background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15), rgba(255,255,255,0.15) 1px, transparent 1px, transparent 3px)' }} /> Post-Service Target</span>
              </div>
            </GlassCard>
            {/* Right column (2 cols) */}
            <div className="col-span-2 flex flex-col gap-5">
              <div className="rounded-2xl p-6 text-center flex-1 flex flex-col justify-center" style={{ background: "linear-gradient(135deg, #064E3B, #047857)" }}>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-300/80 mb-2">{t.sample_score_label}</p>
                <p className="text-7xl font-black text-white leading-none">8.1<span className="text-3xl text-emerald-300">/10</span></p>
                <p className="text-emerald-200 font-bold mt-2 text-lg">{t.sample_score_status}</p>
              </div>
              <GlassCard dark className="p-5">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{t.sample_valuation}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-black text-white">-1.4%</p>
                    <p className="text-xs text-slate-500 mt-1">Valuation Impact</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-black text-emerald-400">Low</p>
                    <p className="text-xs text-slate-500 mt-1">NPA Risk Level</p>
                  </div>
                </div>
              </GlassCard>
              <div className="rounded-2xl p-5 border border-blue-500/20" style={{ background: "linear-gradient(135deg, rgba(0,51,153,0.3), rgba(0,100,200,0.15))" }}>
                <p className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-2">{t.sample_recommendation}</p>
                <p className="text-sm text-blue-100/80 leading-relaxed">{t.sample_recommendation_text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 6: Bank Use Cases ──
  {
    id: "usecases",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(160deg, #080C1A 0%, #0D1B3E 40%, #132B5E 100%)" }}>
        <FloatingOrbs colors={["#3B82F6", "#F59E0B", "#10B981"]} opacity={0.08} />
        <GridPattern color="rgba(255,255,255,0.02)" />

        <div className="relative flex flex-col h-full px-20 py-14 z-10">
          <Badge color="gold">{t.usecase_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-10 tracking-tight">
            {t.usecase_title} <GradientText from="#FFD700" to="#FFAA00">{t.usecase_accent}</GradientText>
          </h2>
          <div className="grid grid-cols-3 gap-6 flex-1">
            {[
              {
                icon: FileCheck, title: t.usecase1_title, gradient: "linear-gradient(135deg, #1E40AF, #3B82F6)", glow: "#3B82F6", accentBorder: "rgba(59,130,246,0.3)",
                desc: t.usecase1_desc,
                points: ["Adjust LTV based on engine health", "Flag high-risk vehicles pre-approval", "Standardize across all branches"],
                pointColor: "#60A5FA",
              },
              {
                icon: TrendingDown, title: t.usecase2_title, gradient: "linear-gradient(135deg, #B45309, #F59E0B)", glow: "#F59E0B", accentBorder: "rgba(245,158,11,0.3)",
                desc: t.usecase2_desc,
                points: ["Early warning for engine degradation", "Proactive portfolio risk management", "Data-backed restructuring decisions"],
                pointColor: "#FBBF24",
              },
              {
                icon: BarChart3, title: t.usecase3_title, gradient: "linear-gradient(135deg, #047857, #10B981)", glow: "#10B981", accentBorder: "rgba(16,185,129,0.3)",
                desc: t.usecase3_desc,
                points: ["Mass assessment at scale", "Quarterly portfolio health monitoring", "Regulatory compliance documentation"],
                pointColor: "#34D399",
              },
            ].map((item, i) => (
              <div key={i} className="rounded-3xl overflow-hidden flex flex-col group hover:-translate-y-1 transition-all duration-500 relative"
                style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))", border: `1px solid ${item.accentBorder}` }}>
                {/* Top glow */}
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-[60px] opacity-20" style={{ background: item.glow }} />
                <div className="p-8 flex-1 flex flex-col relative z-10">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: item.gradient, boxShadow: `0 8px 24px ${item.glow}30` }}>
                    <item.icon size={26} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 mb-5 leading-relaxed text-[15px]">{item.desc}</p>
                  <ul className="space-y-2.5 mt-auto">
                    {item.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: item.pointColor }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 7: Sound AI Deep Dive (with waveform visuals) ──
  {
    id: "sound-ai",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(160deg, #020617 0%, #0C1A3D 40%, #0F2350 100%)" }}>
        <FloatingOrbs colors={["#3B82F6", "#06B6D4", "#8B5CF6"]} opacity={0.1} />

        <div className="relative flex flex-col h-full px-20 py-12 z-10">
          <Badge color="blue">{t.soundai_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-8 tracking-tight">
            {t.soundai_title} <GradientText from="#60A5FA" to="#06B6D4">{t.soundai_accent}</GradientText>
          </h2>

          <div className="grid grid-cols-2 gap-8 flex-1">
            {/* Left: Spectrogram / Waveform visualization */}
            <div className="flex flex-col gap-5">
              {/* Main waveform display */}
              <div className="rounded-2xl p-5 flex-1 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(59,130,246,0.08), rgba(6,182,212,0.04))", border: "1px solid rgba(59,130,246,0.15)" }}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-400/60 mb-3">Live Audio Waveform — Engine Running</p>
                {/* Waveform visualization */}
                <div className="flex items-center justify-center gap-[2px] h-20 mb-4">
                  {[15,25,40,30,55,70,45,80,60,90,50,75,35,65,85,55,70,40,60,45,80,55,90,65,75,40,55,70,45,85,60,50,35,65,75,55,80,45,70,60,85,50,40,65,30,55,45,70,35,60,50,75,40,55,45,35,25,40,30,20].map((h, i) => (
                    <div key={i} className="w-[3px] rounded-full" style={{
                      height: `${h}%`,
                      background: `linear-gradient(to top, #1E40AF, ${h > 70 ? '#F59E0B' : h > 50 ? '#06B6D4' : '#3B82F6'})`,
                      opacity: 0.5 + (h / 100) * 0.5,
                      boxShadow: h > 70 ? '0 0 6px rgba(245,158,11,0.3)' : 'none',
                    }} />
                  ))}
                </div>
                {/* Frequency labels */}
                <div className="flex justify-between text-[9px] text-blue-400/30 font-mono">
                  <span>0 Hz</span><span>500 Hz</span><span>2 kHz</span><span>8 kHz</span><span>16 kHz</span>
                </div>
              </div>

              {/* Spectrogram bars */}
              <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(139,92,246,0.08), rgba(59,130,246,0.04))", border: "1px solid rgba(139,92,246,0.15)" }}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-violet-400/60 mb-3">Frequency Decomposition — 16 Sound Types</p>
                <div className="grid grid-cols-8 gap-1 h-16">
                  {[
                    { h: 45, label: "Knock", color: "#10B981" },
                    { h: 30, label: "Grind", color: "#10B981" },
                    { h: 70, label: "Rattle", color: "#F59E0B" },
                    { h: 25, label: "Whine", color: "#10B981" },
                    { h: 55, label: "Click", color: "#F59E0B" },
                    { h: 20, label: "Hiss", color: "#10B981" },
                    { h: 65, label: "Rumble", color: "#F59E0B" },
                    { h: 35, label: "Belt", color: "#10B981" },
                  ].map((bar, i) => (
                    <div key={i} className="flex flex-col items-center justify-end gap-1">
                      <div className="w-full rounded-t-md relative" style={{
                        height: `${bar.h}%`,
                        background: `linear-gradient(to top, ${bar.color}40, ${bar.color})`,
                        boxShadow: `0 0 8px ${bar.color}30`,
                      }}>
                        {bar.h > 50 && <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full" style={{ background: bar.color, boxShadow: `0 0 6px ${bar.color}` }} />}
                      </div>
                      <span className="text-[8px] text-slate-500 font-medium">{bar.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-3 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Normal</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />Elevated</span>
                </div>
              </div>
            </div>

            {/* Right: Process + key differentiators */}
            <div className="flex flex-col gap-4">
              {/* 3 Steps compact */}
              <div className="space-y-2.5">
                {[
                  { step: "1", title: "Record", desc: "60-sec video from any smartphone", icon: Mic, color: "#3B82F6" },
                  { step: "2", title: "AI Analyzes", desc: "16 sound types vs brand baselines", icon: Brain, color: "#8B5CF6" },
                  { step: "3", title: "Report", desc: "Health score + valuation impact", icon: FileCheck, color: "#10B981" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 rounded-xl px-4 py-3 bg-white/[0.04] border border-white/[0.06]">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}20`, border: `1px solid ${item.color}30` }}>
                      <span className="text-sm font-black" style={{ color: item.color }}>{item.step}</span>
                    </div>
                    <div>
                      <span className="text-sm font-bold text-white">{item.title}</span>
                      <span className="text-xs text-slate-500 ml-2">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Key tech points */}
              <GlassCard dark className="p-5 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Key Differentiators</p>
                <div className="space-y-3">
                  {[
                    { icon: Award, text: "Patented acoustic classification algorithm", color: "#FFD700" },
                    { icon: Target, text: "Brand & model calibrated baselines (Toyota ≠ Isuzu)", color: "#10B981" },
                    { icon: Brain, text: "Dual AI cross-verification (GPT-4o + Claude)", color: "#8B5CF6" },
                    { icon: Shield, text: "Tamper-proof — same video = same score, always", color: "#3B82F6" },
                    { icon: Zap, text: "Results in under 2 minutes, any smartphone", color: "#F59E0B" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <item.icon size={14} style={{ color: item.color }} className="flex-shrink-0" />
                      <span className="text-[13px] text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { val: "16", label: "Sounds" },
                  { val: "8", label: "Categories" },
                  { val: "95%+", label: "Accuracy" },
                  { val: "<2m", label: "Speed" },
                ].map((m, i) => (
                  <div key={i} className="rounded-xl p-2.5 text-center bg-white/[0.04] border border-white/[0.06]">
                    <p className="text-lg font-black"><GradientText from="#FFD700" to="#FFAA00">{m.val}</GradientText></p>
                    <p className="text-[9px] text-slate-500 font-medium">{m.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },

  // ── SLIDE 8: ROI ──
  {
    id: "roi",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #000B2E 0%, #001B6E 40%, #003399 100%)" }}>
        <FloatingOrbs colors={["#FFD700", "#00BFFF", "#FF6B6B"]} opacity={0.12} />
        <GridPattern />

        <div className="relative flex flex-col h-full px-20 py-14 z-10">
          <Badge color="gold">{t.roi_badge}</Badge>
          <h2 className="text-5xl font-black text-white mt-4 mb-10 tracking-tight">
            {t.roi_title} <GradientText from="#FFD700" to="#FFAA00">{t.roi_accent}</GradientText>
          </h2>
          <div className="grid grid-cols-3 gap-6 mb-8">
            <MetricPill value="30-40%" label={t.roi_npa} />
            <MetricPill value="<2 min" label={t.roi_speed} />
            <MetricPill value="100%" label={t.roi_objective} />
          </div>
          <GlassCard dark className="p-8 flex-1 flex flex-col justify-center">
            <h3 className="text-2xl font-bold text-white mb-6">
              {t.roi_example} <GradientText from="#FFD700" to="#FFAA00">{t.roi_example_count}</GradientText>
            </h3>
            <div className="grid grid-cols-3 gap-8">
              <div className="rounded-xl p-5 bg-white/[0.04] border border-white/[0.08]">
                <p className="text-blue-300/80 text-sm mb-2 font-medium">Current NPA Rate (Industry)</p>
                <p className="text-4xl font-black text-white">4-6%</p>
                <p className="text-slate-500 text-sm mt-1">400-600 problem loans</p>
              </div>
              <div className="rounded-xl p-5 bg-emerald-500/[0.08] border border-emerald-500/20">
                <p className="text-emerald-300/80 text-sm mb-2 font-medium">With Wisedrive AI Screening</p>
                <p className="text-4xl font-black text-emerald-400">2.5-4%</p>
                <p className="text-emerald-300/50 text-sm mt-1">Early detection of 150+ high-risk vehicles</p>
              </div>
              <div className="rounded-xl p-5 bg-yellow-500/[0.08] border border-yellow-500/20">
                <p className="text-yellow-300/80 text-sm mb-2 font-medium">Estimated Annual Savings</p>
                <p className="text-4xl font-black"><GradientText from="#FFD700" to="#FFA500">฿15-30M</GradientText></p>
                <p className="text-yellow-300/50 text-sm mt-1">Per year in avoided losses</p>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    ),
  },

  // ── SLIDE 9: CTA ──
  {
    id: "cta",
    render: (t) => (
      <div className="relative h-full overflow-hidden" style={{ background: "linear-gradient(135deg, #000B2E 0%, #001B6E 40%, #003399 70%, #0052CC 100%)" }}>
        <FloatingOrbs colors={["#FFD700", "#FF8C00", "#0088FF"]} opacity={0.15} />
        <GridPattern />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-white/[0.04]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] rounded-full border border-white/[0.03]" />

        <div className="relative flex flex-col items-center justify-center h-full text-white px-20 z-10">
          <img src="/wisedrive-logo-deck.png" alt="Wisedrive" className="h-14 mb-10 drop-shadow-2xl" />
          <h2 className="text-6xl font-black text-center mb-4 tracking-tight">
            {t.cta_title} <GradientText from="#FFD700" to="#FFAA00" className="text-6xl font-black">{t.cta_accent}</GradientText>
          </h2>
          <p className="text-xl text-blue-200/70 text-center mb-12 max-w-2xl leading-relaxed">
            {t.cta_desc}
          </p>
          <div className="grid grid-cols-3 gap-6 mb-14 w-full max-w-2xl">
            {[
              { val: t.cta_free, label: t.cta_pilot },
              { val: t.cta_week, label: t.cta_setup },
              { val: t.cta_dedicated, label: t.cta_support },
            ].map((item, i) => (
              <GlassCard key={i} dark className="p-6 text-center">
                <p className="text-3xl font-black"><GradientText from="#FFD700" to="#FFAA00">{item.val}</GradientText></p>
                <p className="text-sm text-blue-200/60 mt-2 font-medium">{item.label}</p>
              </GlassCard>
            ))}
          </div>
          <div className="text-center">
            <p className="text-lg text-blue-200/60 font-medium">ai.wisedrive.com</p>
            <p className="text-blue-300/40 mt-1">Wisedrive Technologies — Inspect Wise, Drive Smart</p>
          </div>
        </div>
      </div>
    ),
  },
];

/* ══════════════════════════════════════════
   PITCH DECK COMPONENT
   ══════════════════════════════════════════ */

export default function PitchDeckPage() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [lang, setLang] = useState(null); // null = show language picker
  const [showLangPicker, setShowLangPicker] = useState(false);

  // Translation accessor for current language
  const t = {};
  if (lang) {
    Object.keys(T).forEach(key => {
      if (key === "_meta") return;
      t[key] = T[key]?.[lang] ?? T[key]?.en ?? key;
    });
  }

  const goTo = useCallback((idx) => {
    if (idx === current || idx < 0 || idx >= SLIDES.length) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 200);
  }, [current]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); goTo(current + 1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goTo(current - 1); }
      if (e.key === "f" || e.key === "F") { document.documentElement.requestFullscreen?.(); }
      if (e.key === "Escape") { document.exitFullscreen?.(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [current, goTo]);

  const slide = SLIDES[current];

  // Language Picker Modal
  if (!lang) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #000B2E 0%, #001B6E 40%, #003399 70%, #0052CC 100%)", fontFamily: "'Inter', system-ui, sans-serif" }}>
        <FloatingOrbs colors={["#FFD700", "#0066FF", "#00AAFF"]} opacity={0.15} />
        <GridPattern />
        <div className="relative z-10 text-center">
          <img src="/wisedrive-logo-deck.png" alt="Wisedrive" className="h-12 mx-auto mb-8 drop-shadow-2xl" />
          <h2 className="text-4xl font-black text-white mb-3">Select Language</h2>
          <p className="text-blue-200/60 text-lg mb-10">Choose your presentation language</p>
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            {Object.entries(T._meta).map(([code, meta]) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`lang-${code}`}
                className="group rounded-2xl px-6 py-5 text-left transition-all duration-300 bg-white/[0.06] border border-white/[0.1] backdrop-blur-xl hover:bg-white/[0.15] hover:border-white/[0.25] hover:scale-[1.03]"
              >
                <span className="text-3xl block mb-2">{meta.flag}</span>
                <span className="text-lg font-bold text-white block">{meta.name}</span>
                <span className="text-xs text-blue-200/40 uppercase tracking-wider">{code.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className={`h-full transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {slide.render(t)}
      </div>

      {/* Language switcher button (top left) */}
      <div className="absolute top-5 left-7 z-50">
        <button
          onClick={() => setShowLangPicker(!showLangPicker)}
          className="bg-black/20 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10 flex items-center gap-2 hover:bg-black/40 transition-all"
          data-testid="lang-switch-btn"
        >
          <Languages size={14} className="text-white/60" />
          <span className="text-xs text-white/60 font-medium">{T._meta[lang].flag} {T._meta[lang].name}</span>
        </button>
        {showLangPicker && (
          <div className="absolute top-10 left-0 mt-1 bg-black/80 backdrop-blur-xl rounded-xl border border-white/10 p-2 space-y-1 min-w-[160px] shadow-2xl">
            {Object.entries(T._meta).map(([code, meta]) => (
              <button
                key={code}
                onClick={() => { setLang(code); setShowLangPicker(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-3 transition-all ${code === lang ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}`}
              >
                <span className="text-lg">{meta.flag}</span>
                <span className="font-medium">{meta.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom nav bar */}
      <div className="absolute bottom-0 left-0 right-0 h-16 flex items-center justify-center gap-4 z-50"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)" }}>
        <button
          onClick={() => goTo(current - 1)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white backdrop-blur-md transition-all duration-300 border border-white/10 disabled:opacity-30"
          disabled={current === 0}
          data-testid="pitch-prev-btn"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-2 px-3">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              data-testid={`pitch-dot-${i}`}
              className={`rounded-full transition-all duration-300 ${i === current
                ? 'w-8 h-2.5 bg-white shadow-[0_0_12px_rgba(255,255,255,0.5)]'
                : 'w-2.5 h-2.5 bg-white/25 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
        <button
          onClick={() => goTo(current + 1)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white backdrop-blur-md transition-all duration-300 border border-white/10 disabled:opacity-30"
          disabled={current === SLIDES.length - 1}
          data-testid="pitch-next-btn"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Slide counter */}
      <div className="absolute top-5 right-7 z-50">
        <div className="bg-black/20 backdrop-blur-md rounded-full px-3 py-1 border border-white/10">
          <span className="text-xs text-white/60 font-mono font-medium">{current + 1} / {SLIDES.length}</span>
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="absolute bottom-5 right-7 z-50 text-[10px] text-white/25 font-mono tracking-wider">
        ARROWS Navigate | F Fullscreen
      </div>
    </div>
  );
}
